export const SearchFacetOptions = ['all', 'courses', 'people', 'events', 'news', 'research'] as const;

export type SearchFacet = (typeof SearchFacetOptions)[number];

export interface SearchFilter {
  key: string;
  metadataField?: string;
  valueExact?: string;
  valueIn?: string[];
  valueBetween?: {
    min?: string; // User's selected date (YYYY-MM-DD)
    max?: string; // User's selected date (YYYY-MM-DD)
  };
  valueBetweenOverlap?: {
    min?: string; // User's selected start date (YYYY-MM-DD)
    max?: string; // User's selected end date (YYYY-MM-DD)
    startField: string; // Entity's start date field (e.g., "event_start_date")
    endField: string; // Entity's end date field (e.g., "event_end_date")
  };
  varyByYear?: boolean;
  varyByLocation?: boolean;
  varyByStudyPeriod?: boolean;
  nested?: {
    path: string;
    filters: SearchFilter[];
  };
}

export type Range = [number, number];
export type FilterValue = string[] | Range;
export interface SearchResult {
  id: string;
  contentType: string;
  score: number;
  forYou?: boolean;
  pageDetails?: {
    url: string;
    title: string;
    description: string;
  };
  pdfDetails?: {
    url: string;
    title: string;
    description: string;
    size: string;
    relevancyDate: string;
  };
  podcastDetails?: {
    url: string;
    title: string;
    description: string;
    length: string;
    publishedDate: string;
  };
  videoDetails?: {
    url: string;
    title: string;
    description: string;
    length: string;
  };
  courseDetails?: {
    url: string;
    title: string;
    description: string;
    atar?: { type: string; value: string };
    gauarenteedAtar?: string;
    duration: string;
    deliveryModes: string[];
    locations: string[];
    offeringLevel: string;
    courseType: string;
    majors: { title: string; url: string }[];
    availableIn: { title: string; url: string }[];
  };
  personDetails?: {
    url: string;
    name: string;
    staffType: [string];
    email?: string;
    location?: string;
    primaryOrgUnit?: {
      type: 'Faculty' | 'Section';
      name: string;
    };
    imageUrl?: string;
  };
  eventDetails?: {
    url: string;
    title: string;
    description: string;
    imageUrl?: string;
    eventDate: string;
    eventStartDate: string;
    eventEndDate: string;
    relevancyDate: string;
    location: string;
  };
  newsDetails?: {
    url: string;
    title: string;
    description: string;
    type: string;
    imageUrl?: string;
    publishedDate: string;
    lastUpdated: string;
    relevancyDate: string;
  };
  researchDetails?: {
    url: string;
    title: string;
    description: string;
    publishedDate: string;
  };
}

export interface SearchResults {
  results?: SearchResult[];
  resultCount?: number;
  page?: number;
  pageSize?: number;
  activeFacet?: SearchFacet;
  facetResultCounts: {
    [K in SearchFacet]: number | null;
  };
  didYouMean?: string;
  loading?: boolean;
}

export interface SuggestionResult {
  text: string;
  category: string;
}

export interface SuggestionResults {
  results: SuggestionResult[];
  didYouMean?: string;
}

export interface SearchAvailability {
  year: number;
  studyPeriodCodes?: string[];
  locationCodes?: string[];
  deliveryModes?: string[];
}

export interface SearchSortOrder {
  type: 'relevance' | 'field' | 'nested';
  title?: string;
  field?: string;
  order?: 'asc' | 'desc';
  nestedPath?: string;
  nestedFilter?: {
    field: string;
    value: string;
    varyByYear?: boolean;
  };
}

export interface SearchQuery {
  searchTerms: string;
  searchFilters?: SearchFilter[];
  facet?: SearchFacet;
  sortOrder?: SearchSortOrder;
  availability?: SearchAvailability;
  region?: string;
  pageSize?: number;
  page?: number;
}

export interface SuggestionsQuery {
  searchTerms?: string;
  facet?: SearchFacet;
  size?: number;
}
